const ORDER = ['exterior', 'interior', 'all'];
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function partition(ars, k) {
  const n = ars.length;
  if (k >= n) return ars.map(a => [a]);
  if (k <= 1) return [ars.slice()];
  const pre = [0];
  ars.forEach(a => pre.push(pre[pre.length - 1] + a));
  const ideal = pre[n] / k;
  const dp = Array.from({ length: k + 1 }, () => Array(n + 1).fill(Infinity));
  const cut = Array.from({ length: k + 1 }, () => Array(n + 1).fill(0));
  dp[0][0] = 0;
  for (let r = 1; r <= k; r++) {
    for (let j = r; j <= n; j++) {
      for (let i = r - 1; i < j; i++) {
        const v = dp[r - 1][i] + (pre[j] - pre[i] - ideal) ** 2;
        if (v < dp[r][j]) {
          dp[r][j] = v;
          cut[r][j] = i;
        }
      }
    }
  }
  const rows = [];
  let j = n;
  for (let r = k; r > 0; r--) {
    const i = cut[r][j];
    rows.unshift(ars.slice(i, j));
    j = i;
  }
  return rows;
}

function layout(list, W) {
  const n = list.length;
  if (!n || !W) return { pos: [], height: 0 };
  const mobile = W < 640;
  const gap = mobile ? 8 : 12;

  if (mobile) {
    if (n === 1) {
      const h = Math.min(W / (list[0].w / list[0].h), W * 1.3);
      return { pos: [{ x: 0, y: 0, w: W, h }], height: h };
    }
    const cw = (W - gap) / 2;
    const ch = cw / 1.5;
    const pos = [];
    let y = 0;
    let i = 0;
    if (n % 2) {
      pos.push({ x: 0, y: 0, w: W, h: W / 2 });
      y = W / 2 + gap;
      i = 1;
    }
    for (; i < n; i += 2) {
      pos.push({ x: 0, y, w: cw, h: ch }, { x: cw + gap, y, w: cw, h: ch });
      y += ch + gap;
    }
    return { pos, height: y - gap };
  }

  const ratio = n === 1 ? .42 : n === 2 ? .36 : n <= 4 ? .3 : .25;
  const target = Math.max(170, W * ratio);
  const maxH = target * 1.5;
  const ars = list.map(p => p.w / p.h);
  const sum = ars.reduce((a, b) => a + b, 0);
  const k = Math.max(1, Math.min(n, Math.round((sum * target) / W)));
  const pos = [];
  let y = 0;
  for (const row of partition(ars, k)) {
    const rs = row.reduce((a, b) => a + b, 0);
    const free = W - gap * (row.length - 1);
    const h = Math.min(free / rs, maxH);
    let x = 0;
    for (const ar of row) {
      const w = free * (ar / rs);
      pos.push({ x, y, w, h });
      x += w + gap;
    }
    y += h + gap;
  }
  return { pos, height: y - gap };
}

function createViewer(root) {
  const dialog = root.querySelector('[data-lbx-sg-viewer]');
  const track = dialog.querySelector('[data-lbx-sg-track]');
  const index = dialog.querySelector('[data-lbx-sg-index]');
  const prev = dialog.querySelector('[data-lbx-sg-prev]');
  const next = dialog.querySelector('[data-lbx-sg-next]');
  let list = [];
  let current = 0;
  let returnFocus = null;
  let downAt = null;

  function setActive(i) {
    current = i;
    prev.disabled = i <= 0;
    next.disabled = i >= list.length - 1;
    index.querySelectorAll('.lbx-sg-thumb').forEach((thumb, n) => thumb.setAttribute('aria-current', String(n === i)));
    const thumb = index.children[i];
    if (thumb) {
      index.scrollTo({ left: thumb.offsetLeft - (index.clientWidth - thumb.offsetWidth) / 2, behavior: reduced ? 'auto' : 'smooth' });
    }
  }

  function goTo(i) {
    const target = Math.max(0, Math.min(list.length - 1, i));
    track.scrollTo({ left: target * track.clientWidth, behavior: reduced ? 'auto' : 'smooth' });
    setActive(target);
  }

  function cleanup() {
    document.documentElement.style.overflow = '';
    if (returnFocus) {
      returnFocus.focus({ preventScroll: true });
      returnFocus = null;
    }
  }

  function close() {
    dialog.classList.remove('is-open');
    setTimeout(() => {
      if (dialog.open) dialog.close();
      cleanup();
    }, reduced ? 0 : 300);
  }

  function open(photos, start, from) {
    list = photos;
    if (!list.length) return;
    returnFocus = from || document.activeElement;
    track.innerHTML = '';
    index.innerHTML = '';

    list.forEach((photo, i) => {
      const slide = document.createElement('figure');
      slide.className = 'lbx-sg-slide';
      const img = document.createElement('img');
      img.src = photo.large;
      img.alt = photo.alt;
      img.decoding = 'async';
      img.loading = Math.abs(i - start) < 2 ? 'eager' : 'lazy';
      const caption = document.createElement('figcaption');
      caption.textContent = photo.caption;
      slide.append(img, caption);
      track.appendChild(slide);

      if (list.length > 1) {
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'lbx-sg-thumb';
        thumb.setAttribute('aria-label', photo.alt);
        const mini = document.createElement('img');
        mini.src = photo.thumb;
        mini.alt = '';
        mini.loading = 'lazy';
        mini.decoding = 'async';
        thumb.appendChild(mini);
        thumb.addEventListener('click', () => goTo(i));
        index.appendChild(thumb);
      }
    });

    index.hidden = list.length < 2;
    prev.hidden = list.length < 2;
    next.hidden = list.length < 2;
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
    track.scrollLeft = start * track.clientWidth;
    setActive(start);
    dialog.focus();
    void dialog.offsetWidth;
    dialog.classList.add('is-open');
  }

  track.addEventListener('scroll', () => {
    const i = Math.round(track.scrollLeft / track.clientWidth);
    if (i !== current) setActive(i);
  }, { passive: true });

  dialog.addEventListener('close', cleanup);
  dialog.addEventListener('cancel', e => {
    e.preventDefault();
    close();
  });
  dialog.addEventListener('keydown', e => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(current + 1);
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(current - 1);
    }
  });
  dialog.addEventListener('pointerdown', e => {
    downAt = { x: e.clientX, y: e.clientY, onMedia: !!e.target.closest('img, button') };
  });
  dialog.addEventListener('click', e => {
    if (!downAt || downAt.onMedia || e.target.closest('img, button')) return;
    if (Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y) > 8) return;
    close();
  });
  dialog.querySelector('[data-lbx-sg-close]').addEventListener('click', close);
  prev.addEventListener('click', () => goTo(current - 1));
  next.addEventListener('click', () => goTo(current + 1));

  return { open };
}

function initGallery(root) {
  const spread = root.querySelector('[data-lbx-sg-spread]');
  const tabs = root.querySelector('[role="tablist"]');
  const indicator = root.querySelector('.lbx-sg-indicator');
  const viewer = createViewer(root);
  let chapter = root.dataset.active || 'all';
  let visible = [];
  let lastWidth = 0;
  let resizeTimer;
  let preloaded = false;

  const tiles = [...spread.querySelectorAll('.lbx-sg-tile')].map(btn => {
    const img = btn.querySelector('img');
    return {
      btn,
      img,
      type: btn.dataset.type,
      w: Number(btn.dataset.w) || 0,
      h: Number(btn.dataset.h) || 0,
      large: btn.dataset.large,
      caption: btn.querySelector('.lbx-sg-caption').textContent,
      shown: false,
    };
  });

  const asPhoto = t => ({ large: t.large, thumb: t.img.currentSrc || t.img.src, alt: t.img.alt, caption: t.caption });

  tiles.forEach(t => {
    const loaded = () => {
      t.img.classList.add('is-loaded');
      if (!t.w || !t.h) {
        t.w = t.img.naturalWidth || 16;
        t.h = t.img.naturalHeight || 9;
        render();
      }
    };
    if (t.img.complete && t.img.naturalWidth) loaded();
    else t.img.addEventListener('load', loaded, { once: true });
    t.btn.addEventListener('click', () => viewer.open(visible.map(asPhoto), visible.indexOf(t), t.btn));
  });

  function moveIndicator() {
    if (!tabs || !indicator) return;
    const active = tabs.querySelector('[aria-selected="true"]');
    if (!active) return;
    indicator.style.width = `${active.offsetWidth}px`;
    indicator.style.transform = `translateX(${active.offsetLeft}px)`;
  }

  function render(dir = 0) {
    const W = spread.clientWidth;
    visible = tiles.filter(t => chapter === 'all' || t.type === chapter);
    const { pos, height } = layout(visible.map(t => ({ w: t.w || 16, h: t.h || 9 })), W);
    const shift = reduced ? 0 : (W < 640 ? 32 : 56);
    let entering = 0;
    let leaving = 0;

    tiles.forEach(t => {
      const i = visible.indexOf(t);
      const style = t.btn.style;
      if (i === -1) {
        if (t.shown) {
          style.transitionDelay = `${leaving * 35}ms`;
          style.setProperty('--dx', `${-dir * shift}px`);
          style.setProperty('--s', dir ? '1' : '.94');
          leaving++;
        }
        t.btn.classList.remove('is-in');
        t.btn.inert = true;
        t.shown = false;
        return;
      }
      const g = pos[i];
      const place = () => {
        style.setProperty('--x', `${g.x}px`);
        style.setProperty('--y', `${g.y}px`);
        style.setProperty('--w', `${g.w}px`);
        style.setProperty('--h', `${g.h}px`);
      };
      if (!t.shown) {
        style.transition = 'none';
        place();
        style.setProperty('--dx', `${dir * shift}px`);
        style.setProperty('--s', dir ? '1' : '.94');
        void t.btn.offsetWidth;
        style.transition = '';
        style.transitionDelay = `${(dir ? 220 : 140) + entering * 60}ms`;
        entering++;
      } else {
        style.transitionDelay = '0ms';
        place();
      }
      style.setProperty('--dx', '0px');
      style.setProperty('--s', '1');
      t.img.sizes = `${Math.ceil(g.w)}px`;
      t.btn.classList.add('is-in');
      t.btn.inert = false;
      t.shown = true;
    });

    spread.style.height = `${height}px`;
  }

  function setChapter(next) {
    if (next === chapter) return;
    const dir = Math.sign(ORDER.indexOf(next) - ORDER.indexOf(chapter));
    chapter = next;
    tabs.querySelectorAll('[role="tab"]').forEach(tab => {
      const on = tab.dataset.chapter === next;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
    });
    moveIndicator();
    render(dir);
  }

  function preloadAll() {
    if (preloaded) return;
    preloaded = true;
    tiles.forEach(t => {
      t.img.loading = 'eager';
    });
  }

  if (tabs) {
    tabs.querySelectorAll('[role="tab"]').forEach(tab => {
      tab.addEventListener('click', () => setChapter(tab.dataset.chapter));
      tab.addEventListener('keydown', e => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const all = [...tabs.querySelectorAll('[role="tab"]')];
        const target = all[(all.indexOf(tab) + (e.key === 'ArrowRight' ? 1 : -1) + all.length) % all.length];
        target.focus();
        setChapter(target.dataset.chapter);
      });
    });
    tabs.addEventListener('pointerenter', preloadAll, { once: true });
    tabs.addEventListener('focusin', preloadAll, { once: true });
  }

  root.querySelector('[data-lbx-sg-full]').addEventListener('click', e => viewer.open(visible.map(asPhoto), 0, e.currentTarget));

  tiles.forEach(t => t.btn.classList.remove('hidden'));
  spread.classList.add('is-ready', 'is-resizing');
  render();
  moveIndicator();
  lastWidth = spread.clientWidth;
  resizeTimer = setTimeout(() => spread.classList.remove('is-resizing'), 150);

  new ResizeObserver(() => {
    if (spread.clientWidth === lastWidth) return;
    lastWidth = spread.clientWidth;
    spread.classList.add('is-resizing');
    render();
    moveIndicator();
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => spread.classList.remove('is-resizing'), 150);
  }).observe(spread);

  new IntersectionObserver((entries, io) => {
    if (!entries.some(e => e.isIntersecting)) return;
    preloadAll();
    io.disconnect();
  }, { rootMargin: '600px 0px' }).observe(spread);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-lbx-sg]').forEach(initGallery);
});
